import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TotalReachLogo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { useExperiment, trackExperimentClick } from "../lib/experiments";
import {
  ChevronDown, Menu, X, Sparkles, Tv, HandCoins, BrainCircuit, Radar, Briefcase,
  Users, HeartHandshake, ShieldCheck, MessageSquare, Palette, Trophy, CalendarDays,
  Mail, Phone,
} from "lucide-react";

/**
 * PublicNav — shared marketing-site top navigation.
 *
 * Used on Landing, Pricing, WhatsNew, PublicDirectory, Login, etc — anywhere
 * a signed-out visitor might land. Includes:
 *   - Product mega-menu (features grouped by pillar)
 *   - Solutions mini-menu (Candidates / Campaigns / Agencies)
 *   - Pricing / Directory / What's New quick links
 *   - Book a Demo / Sign Up / Sign In actions
 *   - Mobile hamburger drawer
 *
 * Props:
 *   compact   {boolean} — hides the top contact strip (used on Pricing/WhatsNew)
 */

const PRODUCT_GROUPS = [
  {
    label: "Command",
    items: [
      { to: "/#feature-explorer", icon: Sparkles, t: "Ask TotalReach", d: "AI Chief of Staff" },
      { to: "/#feature-explorer", icon: BrainCircuit, t: "AI Assistant", d: "Plan in plain English" },
      { to: "/#feature-explorer", icon: Radar, t: "War Room", d: "Live cross-channel delivery" },
    ],
  },
  {
    label: "Advertise",
    items: [
      { to: "/#feature-explorer", icon: Tv, t: "CTV Campaigns", d: "TV-quality video, every screen" },
      { to: "/#feature-explorer", icon: MessageSquare, t: "Political Intel", d: "Opposition & PAC alerts" },
      { to: "/#feature-explorer", icon: Palette, t: "Ad Studio", d: "AI creative in minutes" },
    ],
  },
  {
    label: "Fundraise",
    items: [
      { to: "/#feature-explorer", icon: HandCoins, t: "Fundraising", d: "Stripe donations + refunds" },
      { to: "/#feature-explorer", icon: MessageSquare, t: "Text-to-Give", d: "SMS keyword → donate" },
      { to: "/#feature-explorer", icon: HeartHandshake, t: "Microsites", d: "Public /m/ landing pages" },
    ],
  },
  {
    label: "Operate",
    items: [
      { to: "/#feature-explorer", icon: CalendarDays, t: "Events", d: "RSVPs + shift signup" },
      { to: "/#feature-explorer", icon: Trophy, t: "Volunteers", d: "Roster, leaderboard, portal" },
      { to: "/#feature-explorer", icon: ShieldCheck, t: "Compliance", d: "FEC + audit vault" },
    ],
  },
];

const SOLUTION_ITEMS = [
  { key: "candidate", icon: HandCoins, t: "For Candidates", d: "Solo races — one campaign, one login.", to: "/pricing#tier-candidate" },
  { key: "campaign", icon: BrainCircuit, t: "For Campaign Managers", d: "The full operating system for one race.", to: "/pricing#tier-campaign_pro" },
  { key: "agency", icon: Briefcase, t: "For Agencies & Consultants", d: "One dashboard, every client.", to: "/pricing#tier-agency" },
];

const NAV_LINKS = [
  { to: "/pricing", label: "Pricing", testid: "public-nav-pricing" },
  { to: "/directory", label: "Directory", testid: "public-nav-directory" },
  { to: "/whats-new", label: "What's New", testid: "public-nav-whats-new" },
];

export function PublicNav({ compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null); // "product" | "solutions" | null
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  // A/B test: "Solutions" vs "Who It's For" — variant is stable per visitor.
  const solutionsVariant = useExperiment("nav_solutions_copy", [
    "solutions",
    "who_its_for",
  ]);
  const solutionsLabel = solutionsVariant === "who_its_for" ? "Who It's For" : "Solutions";

  // Close on route change
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!openMenu) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openMenu]);

  const go = (to) => {
    setOpenMenu(null);
    setMobileOpen(false);
    if (to.startsWith("/#") || to.startsWith("#")) {
      // Anchor on landing page
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const id = to.replace(/.*#/, "");
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 350);
      } else {
        const id = to.replace(/.*#/, "");
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }
    navigate(to);
  };

  const isActive = (to) => location.pathname === to;

  return (
    <>
      {/* Contact strip */}
      {!compact && (
        <div className="border-b border-white/5 bg-sidebar" data-testid="public-nav-contact-strip">
          <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-end gap-6 text-[11px] text-slate-400">
            <a href="mailto:info@totalreach.us" className="inline-flex items-center gap-1.5 hover:text-cyan-300">
              <Mail className="w-3 h-3" /> info@totalreach.us
            </a>
            <a href="tel:+14699911289" className="inline-flex items-center gap-1.5 hover:text-cyan-300">
              <Phone className="w-3 h-3" /> +1 (469) 991-1289
            </a>
          </div>
        </div>
      )}

      {/* Main nav */}
      <header
        className="sticky top-0 z-40 border-b border-white/10 bg-app/85 backdrop-blur-xl"
        data-testid="public-nav"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4" ref={menuRef}>
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            data-testid="public-nav-logo"
            className="shrink-0"
            aria-label="TotalReach home"
          >
            <TotalReachLogo size={28} />
          </button>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1 text-sm text-slate-300 flex-1 justify-center">
            {/* Product mega-menu */}
            <MenuTrigger
              label="Product"
              open={openMenu === "product"}
              onClick={() => setOpenMenu(openMenu === "product" ? null : "product")}
              testid="public-nav-product"
            />
            {/* Solutions mini-menu — label A/B tested */}
            <MenuTrigger
              label={solutionsLabel}
              open={openMenu === "solutions"}
              onClick={() => {
                const willOpen = openMenu !== "solutions";
                setOpenMenu(willOpen ? "solutions" : null);
                if (willOpen) {
                  trackExperimentClick("nav_solutions_copy", solutionsVariant, {
                    action: "open_menu",
                  });
                }
              }}
              testid="public-nav-solutions"
            />
            {NAV_LINKS.map((l) => (
              <button
                key={l.to}
                onClick={() => go(l.to)}
                data-testid={l.testid}
                className={`px-3 py-2 rounded-md transition-colors ${
                  isActive(l.to) ? "text-cyan-300" : "hover:text-cyan-300"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <ThemeToggle className="mr-1" />
            <button
              onClick={() => navigate("/request-demo")}
              data-testid="public-nav-book-demo"
              className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2 text-sm hover:opacity-90 transition-opacity"
            >
              Book a Demo
            </button>
            <button
              onClick={() => navigate("/signup")}
              data-testid="public-nav-signup"
              className="border border-cyan-400/40 text-cyan-300 rounded-full px-5 py-2 text-sm hover:bg-cyan-400/10 transition-colors"
            >
              Sign Up
            </button>
            <button
              onClick={() => navigate("/login")}
              data-testid="public-nav-signin"
              className="border border-white/25 text-white font-semibold rounded-full px-5 py-2 text-sm hover:border-cyan-400/60 hover:text-cyan-300 transition-colors"
            >
              Log In
            </button>
          </div>

          {/* Mobile: visible Log In + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle className="md:hidden" />
            <button
              onClick={() => navigate("/login")}
              data-testid="public-nav-mobile-login"
              className="border border-white/25 text-white font-semibold rounded-full px-4 py-1.5 text-sm hover:border-cyan-400/60 hover:text-cyan-300 transition-colors"
            >
              Log In
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="public-nav-mobile-toggle"
              className="text-slate-300 hover:text-white p-2"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Product mega-menu panel */}
          {openMenu === "product" && (
            <div
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[min(96vw,900px)] rounded-2xl border border-white/10 bg-elevated shadow-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6"
              data-testid="public-nav-product-panel"
            >
              {PRODUCT_GROUPS.map((g) => (
                <div key={g.label}>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">
                    {g.label}
                  </div>
                  <div className="space-y-1">
                    {g.items.map((it) => (
                      <button
                        key={it.t}
                        onClick={() => go(it.to)}
                        data-testid={`public-nav-product-${it.t.replace(/\s+/g, "-").toLowerCase()}`}
                        className="w-full text-left rounded-lg px-2 py-2 hover:bg-white/5 flex gap-2 items-start"
                      >
                        <it.icon className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-sm text-white font-medium">{it.t}</span>
                          <span className="block text-[11px] text-slate-500 leading-tight">{it.d}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="col-span-2 md:col-span-4 border-t border-white/10 pt-3 flex items-center justify-between text-xs">
                <span className="text-slate-500">All modules included — pick your plan by team size.</span>
                <button
                  onClick={() => navigate("/pricing")}
                  className="text-cyan-300 hover:text-cyan-100 font-medium"
                  data-testid="public-nav-product-see-pricing"
                >
                  See pricing →
                </button>
              </div>
            </div>
          )}

          {/* Solutions panel */}
          {openMenu === "solutions" && (
            <div
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[min(96vw,640px)] rounded-2xl border border-white/10 bg-elevated shadow-2xl p-5 grid md:grid-cols-3 gap-3"
              data-testid="public-nav-solutions-panel"
            >
              {SOLUTION_ITEMS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => go(s.to)}
                  data-testid={`public-nav-solutions-${s.key}`}
                  className="text-left rounded-xl border border-white/10 hover:border-cyan-400/40 hover:bg-white/[0.03] p-4 transition-colors"
                >
                  <s.icon className="w-5 h-5 text-cyan-300" />
                  <div className="text-sm text-white font-semibold mt-2">{s.t}</div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-snug">{s.d}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 bg-sidebar" data-testid="public-nav-mobile-drawer">
            <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
              <div className="pb-3 border-b border-white/10 mb-3 grid grid-cols-1 gap-2">
                <button
                  onClick={() => { setMobileOpen(false); navigate("/login"); }}
                  data-testid="mobile-nav-signin"
                  className="w-full border border-white/25 text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:border-cyan-400/60 hover:text-cyan-300"
                >
                  Log In
                </button>
                <button
                  onClick={() => { setMobileOpen(false); navigate("/signup"); }}
                  data-testid="mobile-nav-signup"
                  className="w-full border border-cyan-400/40 text-cyan-300 rounded-full px-5 py-2.5 text-sm"
                >
                  Sign Up
                </button>
                <button
                  onClick={() => { setMobileOpen(false); navigate("/request-demo"); }}
                  data-testid="mobile-nav-book-demo"
                  className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm"
                >
                  Book a Demo
                </button>
              </div>
              <MobileGroup label="Product">
                {PRODUCT_GROUPS.flatMap((g) => g.items).map((it) => (
                  <MobileLink key={it.t} onClick={() => go(it.to)} testid={`mobile-nav-product-${it.t.replace(/\s+/g, "-").toLowerCase()}`}>
                    <it.icon className="w-4 h-4 text-cyan-300" /> {it.t}
                  </MobileLink>
                ))}
              </MobileGroup>
              <MobileGroup label={solutionsLabel}>
                {SOLUTION_ITEMS.map((s) => (
                  <MobileLink key={s.key} onClick={() => go(s.to)} testid={`mobile-nav-solutions-${s.key}`}>
                    <s.icon className="w-4 h-4 text-cyan-300" /> {s.t}
                  </MobileLink>
                ))}
              </MobileGroup>
              {NAV_LINKS.map((l) => (
                <MobileLink key={l.to} onClick={() => go(l.to)} testid={`mobile-${l.testid}`}>
                  {l.label}
                </MobileLink>
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
}

const MenuTrigger = ({ label, open, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-2 rounded-md inline-flex items-center gap-1 transition-colors ${
      open ? "text-cyan-300 bg-white/[0.04]" : "hover:text-cyan-300"
    }`}
    aria-expanded={open}
  >
    {label}
    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
  </button>
);

const MobileGroup = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mt-3 mb-1 px-2">{label}</div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const MobileLink = ({ onClick, testid, children }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full text-left px-2 py-2.5 rounded-md text-sm text-slate-200 hover:bg-white/5 hover:text-cyan-300 flex items-center gap-2"
  >
    {children}
  </button>
);
